import { useEffect, useMemo, useRef, useState } from"react";
import { HelpCircle, Search, X, Lightbulb, ExternalLink } from"lucide-react";
import { FAQ as FAQ_ITEMS, FAQ_CATEGORIES } from"@/data/faq";
import { getFaqAccountRefs } from"@/lib/faqLinks";
import { PCG_INDEX } from"@/lib/pcgUtils";

function norm(s) {
 return s
 .toLowerCase()
 .normalize("NFD")
 .replace(/[\u0300-\u036f]/g,"");
}

// Surligne les codes comptes (entre backticks) dans une cellule de texte
function renderCell(text) {
 if (!text || typeof text !=="string") return text;
 const parts = text.split(/(`[^`]+`)/g);
 return parts.map((p, i) => {
 if (p.startsWith("`") && p.endsWith("`")) {
 return (
 <code
 key={i}
 className="px-1.5 py-0.5 rounded bg-slate-100 text-[#15294F] font-mono text-[11px] font-bold border border-keyto"
 >
 {p.slice(1, -1)}
 </code>
 );
 }
 return <span key={i}>{p}</span>;
 });
}

export default function FAQ({ deepLinkId, onSelectAccount }) {
 const [q, setQ] = useState("");
 const [activeCat, setActiveCat] = useState("Toutes");
 const [openId, setOpenId] = useState(deepLinkId || null);
 const cardRefs = useRef({});

 // Si un deepLink arrive (depuis une fiche compte), on déplie + scroll
 useEffect(() => {
 if (!deepLinkId) return;
 setOpenId(deepLinkId);
 setActiveCat("Toutes");
 setQ("");
 setTimeout(() => {
 const el = cardRefs.current[deepLinkId];
 if (el) el.scrollIntoView({ behavior:"smooth", block:"start" });
 }, 80);
 }, [deepLinkId]);

 const filtered = useMemo(() => {
 const nq = norm(q.trim());
 return FAQ_ITEMS.filter((f) => {
 if (activeCat !=="Toutes" && f.cat !== activeCat) return false;
 if (!nq) return true;
 const hay = [
 f.titre,
 f.contexte,
 f.memo,
 f.exemple,
 ...f.cols.map((c) => c.label),
 ...f.rows.flatMap((r) => [r.critere, ...r.cells]),
 ]
 .join("")
 .toLowerCase();
 return norm(hay).includes(nq);
 });
 }, [q, activeCat]);

 return (
 <div data-testid="faq-view" className="space-y-8">
 {/* Hero */}
 <div className="bg-gradient-to-br from-white to-slate-50 border border-keyto rounded-2xl shadow-keyto p-6 md:p-8">
 <div className="flex items-start gap-4">
 <div className="shrink-0 w-12 h-12 rounded-xl bg-[#A56A2E]/10 border border-[#A56A2E]/30 flex items-center justify-center">
 <HelpCircle className="text-[#A56A2E]" size={22} />
 </div>
 <div className="flex-1">
 <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full border border-[#A56A2E]/30 bg-amber-50 text-[#A56A2E] mb-3">
 Questions fréquentes
 </span>
 <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-[#15294F] leading-tight">
 Comparatifs et concepts clés
 </h1>
 <p className="mt-3 text-slate-600 max-w-2xl">
 Les confusions les plus fréquentes en comptabilité, expliquées
 côte à côte. <strong>{FAQ_ITEMS.length} fiches comparatives</strong> sur
 les notions où l&apos;on se trompe le plus souvent.
 </p>
 </div>
 </div> {/* Search */}
 <div className="mt-6 relative max-w-2xl">
 <Search
 size={16}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
 />
 <input
 data-testid="faq-search"
 type="text"
 value={q}
 onChange={(e) => setQ(e.target.value)}
 placeholder="Rechercher : amortissement, opex, marge, tva..."
 className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-keyto bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2D6BE0]/30"
 />
 {q && (
 <button
 onClick={() => setQ("")}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
 >
 <X size={14} />
 </button>
 )}
 </div>

 {/* Categories */}
 <div className="mt-4 flex flex-wrap gap-2">
 {["Toutes", ...FAQ_CATEGORIES].map((cat) => (
 <button
 key={cat}
 data-testid={`faq-cat-${cat}`}
 onClick={() => setActiveCat(cat)}
 className={`text-xs px-3 py-1.5 rounded-full font-semibold border transition ${
 activeCat === cat
 ?"bg-[#15294F] text-white border-[#15294F]"
 :"bg-white text-slate-700 border-keyto hover:border-[#2D6BE0]/40"
 }`}
 >
 {cat}
 </button>
 ))}
 </div>
 </div>

 {/* Results */}
 {filtered.length === 0 ? (
 <div className="text-center py-12 text-slate-500 text-sm border border-dashed border-keyto rounded-xl">
 Aucune fiche ne correspond à votre recherche.
 </div>
 ) : (
 <div className="space-y-5">
 {filtered.map((f) => {
 const isOpen = openId === f.id;
 const accRefs = isOpen ? [...getFaqAccountRefs(f)].filter((n) => PCG_INDEX[n]) : [];
 return (
 <article
 key={f.id}
 ref={(el) => { if (el) cardRefs.current[f.id] = el; }}
 data-testid={`faq-card-${f.id}`}
 className="bg-white border border-keyto rounded-2xl shadow-keyto overflow-hidden scroll-mt-24"
 >
 {/* Header (clickable) */}
 <button
 onClick={() => setOpenId(isOpen ? null : f.id)}
 className="w-full text-left p-5 md:p-6 hover:bg-slate-50 transition flex items-start justify-between gap-4"
 >
 <div className="flex-1">
 <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-slate-100 text-slate-600 border border-keyto mb-2">
 {f.cat}
 </span>
 <h2 className="font-heading text-xl md:text-2xl font-extrabold text-[#15294F]">
 {f.titre}
 </h2>
 <p className="mt-1.5 text-sm text-slate-600">{f.contexte}</p>
 </div>
 <div className="shrink-0 mt-1 text-[#2D6BE0] text-xs font-bold uppercase tracking-wide">
 {isOpen ?"Replier" :"Comparer"}
 </div>
 </button>

 {isOpen && (
 <div className="px-5 md:px-6 pb-6 space-y-5">
 {/* Comparison table */}
 <div className="overflow-x-auto -mx-5 md:-mx-6 px-5 md:px-6">
 <table className="w-full text-sm border-collapse">
 <thead>
 <tr>
 <th className="text-left py-3 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b-2 border-keyto w-44 align-bottom">
 Critère
 </th>
 {f.cols.map((c, i) => (
 <th
 key={i}
 className="text-left py-3 px-3 text-xs font-extrabold border-b-2 align-bottom"
 style={{
 color: c.color,
 borderBottomColor: c.color,
 }}
 >
 {c.label}
 </th>
 ))}
 </tr>
 </thead>
 <tbody>
 {f.rows.map((row, ri) => (
 <tr
 key={ri}
 className={ri % 2 === 0 ?"bg-slate-50/50" :""}
 >
 <td className="py-2.5 px-3 text-xs font-semibold text-slate-700 border-b border-keyto align-top">
 {row.critere}
 </td>
 {row.cells.map((cell, ci) => (
 <td
 key={ci}
 className="py-2.5 px-3 text-sm text-[#15294F] border-b border-keyto align-top"
 >
 {renderCell(cell)}
 </td>
 ))}
 </tr>
 ))}
 </tbody>
 </table>
 </div>

 {/* Example */}
 {f.exemple && (
 <div className="bg-blue-50/60 border-l-4 border-[#2D6BE0] rounded-r-lg p-4">
 <p className="text-[10px] font-bold uppercase tracking-wider text-[#2D6BE0] mb-1.5">
 Exemple concret
 </p>
 <p className="text-sm text-[#15294F] leading-relaxed">
 {renderCell(f.exemple)}
 </p>
 </div>
 )}

 {/* Memo */}
 {f.memo && (
 <div className="flex items-start gap-3 bg-[#15294F] text-white rounded-lg p-4">
 <Lightbulb
 size={18}
 className="shrink-0 mt-0.5 text-amber-300"
 />
 <div>
 <p className="text-[10px] font-bold uppercase tracking-wider text-amber-300 mb-1">
 À retenir
 </p>
 <p className="text-sm leading-relaxed">{f.memo}</p>
 </div>
 </div>
 )}

 {/* Comptes mentionnés — lien vers la fiche */}
 {accRefs.length > 0 && onSelectAccount && (
 <div data-testid={`faq-accounts-${f.id}`} className="border-t border-keyto pt-4">
 <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
 Comptes mentionnés — ouvrir la fiche
 </p>
 <div className="flex flex-wrap gap-1.5">
 {accRefs.map((n) => (
 <button
 key={n}
 data-testid={`faq-open-account-${n}`}
 onClick={() => onSelectAccount(n)}
 className="inline-flex items-center gap-1 text-xs font-bold font-mono px-2.5 py-1 rounded-md bg-slate-100 text-[#15294F] hover:bg-[#2D6BE0] hover:text-white border border-keyto transition"
 title={PCG_INDEX[n]?.lib}
 >
 {n}
 <ExternalLink size={10} />
 </button>
 ))}
 </div>
 </div>
 )}
 </div>
 )}
 </article>
 );
 })}
 </div>
 )}
 </div>
 );
}
